import { Checkbox, FormControlLabel, IconButton, ListItem, TextField, Tooltip } from "@mui/material";
import React from "react";
import Avatar from 'react-avatar';
import DeleteIconButton from '../components/DeleteIconButton';
import EditIcon from '@mui/icons-material/BorderColorOutlined';
import { defaultStyles, FileIcon } from 'react-file-icon';
import InlineVideo from './InlineVideo';
import DOMPurify from 'isomorphic-dompurify';
import { escape_html, linkify } from './utils/chat_utils'
import DoneIcon from '@mui/icons-material/Done';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import { cv } from '../themeVars';

const SHARE_WITH_GUIDE_HELP = "Guide notes pass this note to the departure's guides word for word: in the pre-tour notes, or as an update if they already went out.";

class NotesItem extends React.Component {
  constructor(props) {
    super(props);

    this.showIconsStyle = { display: 'block', width: "16px", height: "16px", opacity: .95}
    this.hideIconsStyle = { display: 'none', width: "16px", height: "16px"}
    this.showIconButtonsStyle = {display: null, marginRight: -2, height: 25, width: 25}
    this.hideIconButtonsStyle = {display: 'none', marginRight: -2, height: 25, width: 25}
    
    this.state = {
      message: null,
      tw_identity: "",
      mediaDiv: <div></div>,
      editDeleteIconStyle: this.hideIconButtonsStyle,
      isBeingEdited: false,
      sharingSaving: false
    };

    this.state.message = this.props.message;
    this.state.tw_identity = this.props.tw_identity;
    this.imageRef = React.createRef();
    this.noteTextRef = React.createRef();
    this.timer = null;
  }

  trimFileName(filename, limit = 15, spacer = '.') {
    if(!filename){
      return "";
    }
    const split = filename.indexOf(".");
    const name = filename.substring(0, split);
    const ext = filename.substring(split);

    let trimName = name.substring(0, limit);

    if (name.length > trimName.length)
      trimName = trimName.padEnd(limit + 3, spacer);

    return trimName + ext;
  }

  getAttachmentUrl = async () => {
    if (this.state.message.type === "media") {
      let mediaUrl = await this.state.message.media.getContentTemporaryUrl();
      if (this.state.message.media.contentType.startsWith("image")) {
        return <a href={mediaUrl} target="_blank" rel="noreferrer">
                  <div>
                    <br />
                      <img style={styles.imageDiv} src={mediaUrl} ref={this.imageRef} alt="" width="100%" maxwidth="300px" height="auto" />
                  </div>
                </a>
      }
      else if (this.state.message.media.contentType.startsWith("video")) {
        return <><br /><InlineVideo src={mediaUrl} imageStyle={styles.imageDiv} /></>
      }
      else {
        let ct = this.state.message.media.contentType.split('/')[1];
        return <a style={ styles.mediaLinkText } href={mediaUrl} target="_blank" rel="noreferrer"><br /><div style={{ width: "30px", marginLeft: "auto", marginRight: "auto" }}><FileIcon extension={ct}  {...defaultStyles[ct]} /><div style={{ alignItems: "center" }}></div></div><div style={{ marginLeft: "auto", marginRight: "auto" }}>{this.trimFileName(this.state.message.media.filename)}</div></a>
      }
    }
  };

  onImageLoad = () => { 
    this.props.scrollToBottom?.();
    this.imageRef.current?.removeEventListener('load', this.onImageLoad);
  }
    
  componentDidMount = async () => {
    
    const mediaUrl = await this.getAttachmentUrl();
    this.setState({ mediaDiv: mediaUrl });
    this.imageRef.current?.addEventListener('load', this.onImageLoad);

    // Since Twilio media links expire after 5 minutes, this is a
    // hack to refresh their links every 1 minute in case they are
    // clicked on for viewing or to save.
    // TODO: Consider saving to and fetching from our backend
    if(mediaUrl){
      this.timer = setInterval(
        (async () => {
            console.debug("ChatItem: Media link interval polling refresh");
            const mediaUrl = await this.getAttachmentUrl();
            this.setState({ mediaDiv: mediaUrl });
        }), 60000);
    
        return () => {
            console.debug("ChatItem: Clearing media link timer interval");
            clearInterval(this.timer);
        };
    }
    this.props.scrollToBottom?.();
  };

  componentWillUnmount = async () => {
    if(this.timer){
      console.debug("ChatItem: Clearing media link timer interval");
      clearInterval(this.timer);
    }
  };

  handleNotesUpdate = async (ev) => {
    var noteDidSave = await this.props.handleNotesUpdate(ev, this.props.noteID, this.noteTextRef.current.value);
    this.state.message.internal_notes = this.noteTextRef.current.value;
    if(noteDidSave){
      this.setState({ isBeingEdited: false, editDeleteIconStyle: this.hideIconButtonsStyle });
    }
  }

  // CP-8461: "Share with guide pre-tour" on this note.
  handleShareWithGuide = async (share) => {
    this.setState({ sharingSaving: true });
    try {
      const saved = await this.props.handleShareWithGuide(this.props.noteID, share);
      if (saved) {
        this.setState({ message: { ...this.state.message, share_with_guide: saved.share_with_guide, shared_with_guide_ts: saved.shared_with_guide_ts } });
      }
    } finally {
      this.setState({ sharingSaving: false });
    }
  }

  // Its own row under the note, set off by a rule, so it reads as a setting of the note and not as the
  // note's last words (inline after the text it did, the bubble being pre-wrap).
  renderShareWithGuide() {
    const shared = this.state.message.share_with_guide === true;
    if (!this.props.shareWithGuideAvailable) {
      return shared ? <div style={styles.shareRow}><span style={styles.sharedCaption}>Shared with guide</span></div> : null;
    }
    return (
      <div style={styles.shareRow}>
        <Tooltip arrow placement="bottom-start" title={SHARE_WITH_GUIDE_HELP}>
          <FormControlLabel
            sx={{ ml: 0, mr: 0, '& .MuiFormControlLabel-label': styles.shareLabel(shared) }}
            label="Share with guide pre-tour"
            control={
              <Checkbox
                size="small"
                sx={{ p: 0.25, mr: 0.5 }}
                checked={shared}
                disabled={this.state.sharingSaving}
                onChange={(ev) => this.handleShareWithGuide(ev.target.checked)}
                inputProps={{ 'aria-label': 'Share with guide pre-tour' }}
              />
            }
          />
        </Tooltip>
      </div>
    );
  }

  render() {

    const isOwnMessage = (this.state.message.author.twilio_identity === this.state.tw_identity);

    // The Twilio identity is shown while loading (while waiting for the author name to be retrieved).
    // Rather than show the identiy, show nothing.
    let author = ""; //this.state.message.author;
    let role = "";
    const role_bg_color_map = { 'Admin': cv('tag-teal', '#B7EBE3'), 'Tour Guide': cv('tag-lavender', '#E4E4F9'), 'Guest': cv('tag-pink', '#F8D7DA'), 'Observer': cv('tag-blue', 'lightblue'), '':'' };
    if (this.state.message.author.twilio_identity in this.props.participantsDict){
      author = this.props.participantsDict[this.state.message.author.twilio_identity];
    }
    if (this.state.message.author.twilio_identity in this.props.participantsRoleDict){
      role = this.props.participantsRoleDict[this.state.message.author.twilio_identity];
    }

    let avatarColor = "white";
    if ( !(this.state.message.author.twilio_identity in this.props.participantsAvatarColorDict) ){
      //const l = Object.keys(this.props.participantsAvatarColorDict).length;
      //this.props.participantsAvatarColorDict[this.state.message.author]  = this.props.colorPalette[l];
    }
    else{
      avatarColor = this.props.participantsAvatarColorDict[this.state.message.author.twilio_identity];
    }
    
    let avatarImg = null
    if ( this.state.message.author.twilio_identity in this.props.participantsAvatarImgDict ){
      //avatarImg = this.props.participantsAvatarImgDict[this.state.message.author];
    }
    return (
      <ListItem style={styles.listItem(isOwnMessage)} sx={{ '& a': { wordBreak: "break-all" }, }}
        onMouseEnter={e => {
          if (this.props.showNotesPane) {
            this.setState({ editDeleteIconStyle: this.showIconButtonsStyle })
          }
        }}
        onMouseLeave={e => {
          this.setState({ editDeleteIconStyle: this.hideIconButtonsStyle });
        }}>
        {this.props.nextMsg?.author.twilio_identity !== this.state.message.author.twilio_identity || new Date(this.props.nextMsg?.updated_ts).toDateString() !== new Date(this.state.message.updated_ts).toDateString() ?
          <Avatar style={styles.avatar(isOwnMessage)} name={author} size="25" round={true} color={avatarColor} src={avatarImg} maxInitials={1} textSizeRatio={2} />
          : <div style={styles.avatar(isOwnMessage)} />
        }
        <span style={styles.textBox(isOwnMessage, this.props.prevMsg?.author === this.state.message.author && new Date(this.props.prevMsg?.updated_ts).toDateString() === new Date(this.state.message.updated_ts).toDateString())}>
          <div style={styles.container(isOwnMessage)}>
            {this.props.prevMsg?.author.twilio_identity !== this.state.message.author.twilio_identity || new Date(this.props.prevMsg?.updated_ts).toDateString() !== new Date(this.state.message.updated_ts).toDateString() ?
              <div style={styles.author(isOwnMessage)}>{isOwnMessage ? author + " (You)" : author}
                &nbsp;<span style={{ color: cv('text', 'black'), backgroundColor: role_bg_color_map[role], padding: '2px 8px', borderRadius: '2px' }}>{role}</span>
              </div>
              : ""}
            {this.state.isBeingEdited !== true ? <span dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(linkify(escape_html(this.state.message.internal_notes)), { ADD_ATTR: ['target'] }) }}></span>
              : <div>
                <TextField
                  inputRef={this.noteTextRef}
                  label="Edit Note"
                  defaultValue={this.state.message.internal_notes}
                  style={{ width: "100%", marginTop: "1rem" }}
                  variant="outlined"
                  multiline
                  minRows={3}
                  maxRows={6}
                  //defaultValue={this.state.conversationNotesText}
                  InputLabelProps={{ shrink: true }}
                  onChange={(event) => {}
                  }
                />
              </div>}
            {this.state.mediaDiv}
            {this.renderShareWithGuide()}

            <div style={styles.timestamp(isOwnMessage)}>
              {isOwnMessage ? 
              <div style={{ textAlign: "center", position: "absolute", left: "50%", transform: "translateX(-50%)", bottom: 0, fontSize: 9 }}>
                {this.state.isBeingEdited !== true ?
                  <span>
                    <Tooltip title="Edit Note" arrow placement="top"><IconButton color="primary" style={this.state.editDeleteIconStyle} onClick={(ev) => this.setState({ isBeingEdited: true })}>
                      <EditIcon style={this.showIconsStyle} />
                    </IconButton></Tooltip>
                    <DeleteIconButton
                      title="Delete Note"
                      tooltipProps={{ arrow: true, placement: 'top' }}
                      size="medium"
                      fontSize="inherit"
                      style={this.state.editDeleteIconStyle}
                      sx={{ fontSize: 16 }}
                      onClick={(ev) => this.props.handleDeleteNote(ev, this.props.noteID)}
                    /></span> :
                  <span>
                    <Tooltip title="Save Note" arrow placement="top"><IconButton color="primary" style={this.showIconButtonsStyle} onClick={(ev) => this.handleNotesUpdate(ev)}>
                      <DoneIcon style={this.showIconsStyle} />
                    </IconButton></Tooltip>
                    <Tooltip title="Cancel" arrow placement="top"><IconButton color="secondary" style={this.showIconButtonsStyle} onClick={(ev) => this.setState({ isBeingEdited: false })}>
                      <CancelOutlinedIcon style={this.showIconsStyle} />
                    </IconButton></Tooltip>
                  </span>
                }
              </div> : ""}
              {new Date(this.state.message.updated_ts).toLocaleString([], { timeStyle: 'short' })}
            </div>
          </div>
        </span>
      </ListItem>
    );
  }
}

const styles = {
  textBox: (isOwnMessage, prevMsgHasSameAuthor) => ({
    display: "flex",
    width: "100%",
    paddingLeft: "6px",
    paddingRight: "16px",
    paddingTop: prevMsgHasSameAuthor ? "0px" : "6px",
    flexDirection: "column",
    alignItems: isOwnMessage ? "flex-end" : "flex-start",
  }),
  listItem: (isOwnMessage) => ({
    flexDirection: "row",
    padingTop: 0,
    paddingBottom: 0
  }),
  avatar: (isOwnMessage) => ({
    width: "26px",
    display: isOwnMessage ? "none" : "flex",
    alignSelf: "end"
  }),
  container: (isOwnMessage) => ({
    borderRadius: 12,
    paddingTop: 6,
    paddingBottom: 4,
    paddingLeft: 14,
    paddingRight: 14,
    color: cv('text', "black"), //isOwnMessage ? "white" : "black",
    fontSize: 13,
    backgroundColor: cv('surface-muted', "#F5F5F5"), //isOwnMessage ? "#007AFF" : "#F5F5F5",
    width: "100%",
    overflowWrap: "break-word",
    whiteSpace: "pre-wrap"
  }),
  imageDiv: { borderRadius: 3, boxShadow: "0 4px 8px 0 rgba(0, 0, 0, 0.2), 0 6px 20px 0 rgba(0, 0, 0, 0.19)" },
  author: (isOwnMessage) => ({ 
    //display: isOwnMessage ? "none" : "block",
    fontSize: 13,
    fontWeight: 500,
    color: cv('text', "black"), //isOwnMessage ? "white" : "black",
  }),
  timestamp: (isOwnMessage) => ({
    fontSize: 9,
    color: cv('text', "black"), //isOwnMessage ? "white" : "#8A8A8F",
    textAlign: "right",
    paddingTop: 4
  }),
  shareRow: {
    display: "flex",
    alignItems: "center",
    marginTop: 8,
    paddingTop: 4,
    borderTop: `1px solid ${cv('border', '#E0E0E0')}`,
    whiteSpace: "normal",
  },
  shareLabel: (shared) => ({
    fontSize: 12,
    fontWeight: shared ? 500 : 400,
    color: shared ? cv('accent', '#007AFF') : cv('text-muted', '#666'),
  }),
  sharedCaption: { fontSize: 12, color: cv('text-muted', '#666'), padding: "3px 0" },
  fileIcon: { paddingLeft: "space20", paddingRight: "space50" },
  mediaLinkText: { fontWeight: 'bold', fontSize: '8px', textAlign: "center" },
};

export default NotesItem;
